import Axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios'
import { parseAuthenticationHeader } from 'helpers/Functions'
import { parseApiErrors } from 'utils/errorParser'

// Mirrors SESSION_EXPIRED_CODE in homebase_apis/Errors.
const SESSION_EXPIRED_CODE = 'session_expired'

let onSessionExpired: (() => void) | null = null
let hasHandledSessionExpired = false

const MAX_RETRIES = 2
const RETRY_BASE_DELAY_MS = 500
const RETRY_METHODS = ['get', 'head', 'options']
const RETRY_STATUSES = [429, 502, 503, 504]

type RetryConfig = InternalAxiosRequestConfig & { retryCount?: number }

/**
 * Whether a failed read is sent again: on a dropped connection, a throttled
 * request or a gateway error. API Gateway's own 429 has no CORS headers, so
 * the browser reports it as a dropped connection.
 */
function shouldRetry(error: AxiosError) {
  const config = error.config as RetryConfig | undefined

  if (
    !config ||
    Axios.isCancel(error) ||
    (config.retryCount ?? 0) >= MAX_RETRIES ||
    !RETRY_METHODS.includes((config.method ?? 'get').toLowerCase())
  ) {
    return false
  }

  return !error.response || RETRY_STATUSES.includes(error.response.status)
}

function retry(config: RetryConfig) {
  const retryCount = (config.retryCount ?? 0) + 1
  const delay =
    RETRY_BASE_DELAY_MS * 2 ** (retryCount - 1) +
    Math.random() * RETRY_BASE_DELAY_MS

  return new Promise((resolve) => setTimeout(resolve, delay)).then(() =>
    Axios({ ...config, retryCount } as RetryConfig)
  )
}

Axios.interceptors.request.use((config) => {
  try {
    const variant = window.localStorage.getItem(
      'testEnvironment.active.variant'
    )

    if (variant === 'test') {
      config.headers = config.headers ?? {}
      ;(config.headers as Record<string, string>)['x-use-test-environment'] =
        'true'
    }
  } catch {
    // localStorage might be unavailable (private browsing, SSR) — ignore.
  }

  return config
})

/**
 * Register what to run when the API reports that the session has died.
 */
export function registerSessionExpiredHandler(handler: () => void) {
  onSessionExpired = handler
}

Axios.interceptors.response.use(
  (response) => {
    // A successful response means the session is live again.
    hasHandledSessionExpired = false

    return response
  },
  (error) => {
    if (shouldRetry(error)) {
      return retry(error.config)
    }

    const isSessionExpired
      = error?.response?.status === 401
      && parseApiErrors(error).some((e) => e.code === SESSION_EXPIRED_CODE)

    // Several requests can 401 at once — sign out only once.
    if (isSessionExpired && !hasHandledSessionExpired) {
      hasHandledSessionExpired = true

      onSessionExpired?.()
    }

    return Promise.reject(error)
  }
)

/**
 * Get authentication config based on current path.
 */
export function getDefaultConfig() {
  // for api-access we want default authentication to be bearer token.
  const isApiAccess = window.location.pathname.includes('api-access')
  const queryParams = new URLSearchParams(window.location.search)
  const token = isApiAccess ? queryParams.get('token') : null

  return token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : parseAuthenticationHeader()
}

/**
 * Generic function to handle GET requests, including redirected responses.
 * @param url - The API endpoint to fetch data from.
 * @param config - Optional Axios configuration options,
 * if given it will replace default config.
 * @returns A promise resolving to the final data.
 */
export async function getWithRedirect<T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<{
  data: { data: T }
}> {
  const response = await Axios.get<{ data: T; redirected_to?: string }>(
    url,
    config ?? getDefaultConfig()
  )

  // Check for a redirection and fetch from the redirected URL if present
  if (response.data.redirected_to) {
    const redirectedResponse = await Axios.get<{ data: T }>(
      response.data.redirected_to
    )

    return redirectedResponse
  }

  return response
}

/**
 * Generic function to handle POST requests, including redirected responses.
 * @param url - The API endpoint to fetch data from.
 * @param data - The data to send in the request body.
 * @param config - Optional Axios configuration options,
 * if given it will replace default config.
 * @returns A promise resolving to the final data.
 */
export async function postWithRedirect<T = any>(
  url: string,
  data: any,
  config?: AxiosRequestConfig
): Promise<{
  data: { data: T }
}> {
  const response = await Axios.post<{ data: T; redirected_to?: string }>(
    url,
    data,
    config ?? getDefaultConfig()
  )

  // Check for a redirection and fetch from the redirected URL if present
  if (response.data.redirected_to) {
    const redirectedResponse = await Axios.get<{ data: T }>(
      response.data.redirected_to
    )

    return redirectedResponse
  }

  return response
}

/**
 * Generic function to handle PATCH requests, including redirected responses.
 * @param url - The API endpoint to fetch data from.
 * @param data - The data to send in the request body.
 * @param config - Optional Axios configuration options,
 * if given it will replace default config.
 * @returns A promise resolving to the final data.
 */
export async function patchWithRedirect<T = any>(
  url: string,
  data: any,
  config?: AxiosRequestConfig
): Promise<{
  data: { data: T }
}> {
  const response = await Axios.patch<{ data: T; redirected_to?: string }>(
    url,
    data,
    config ?? getDefaultConfig()
  )

  // Check for a redirection and fetch from the redirected URL if present
  if (response.data.redirected_to) {
    const redirectedResponse = await Axios.get<{ data: T }>(
      response.data.redirected_to
    )

    return redirectedResponse
  }

  return response
}

/**
 * Generic function to handle PUT requests, including redirected responses.
 * @param url - The API endpoint to fetch data from.
 * @param data - The data to send in the request body.
 * @param config - Optional Axios configuration options,
 * if given it will replace default config.
 * @returns A promise resolving to the final data.
 */
export async function putWithRedirect<T = any>(
  url: string,
  data: any,
  config?: AxiosRequestConfig
): Promise<{
  data: { data: T }
}> {
  const response = await Axios.put<{ data: T; redirected_to?: string }>(
    url,
    data,
    config ?? getDefaultConfig()
  )

  // Check for a redirection and fetch from the redirected URL if present
  if (response.data.redirected_to) {
    const redirectedResponse = await Axios.get<{ data: T }>(
      response.data.redirected_to
    )

    return redirectedResponse
  }

  return response
}

/**
 * Generic function to handle GET requests, including redirected responses.
 * Used for internal endpoints which dont include "data" in response.
 * ex. measurements
 * @param url - The API endpoint to fetch data from.
 * @returns A promise resolving to the final data.
 */
export async function getInternalWithRedirect<T = any>(
  url: string
): Promise<AxiosResponse<T>> {
  const response = await Axios.get<T & { redirected_to?: string }>(
    url,
    parseAuthenticationHeader()
  )

  // Check for a redirection and fetch from the redirected URL if present
  if (response.data.redirected_to) {
    const redirectedResponse = await Axios.get<T>(response.data.redirected_to)

    return redirectedResponse
  }

  return response
}

/**
 * Generic function to handle POST requests, including redirected responses.
 * Used for internal endpoints whose payload is returned directly on
 * `response.data` (no nested `data` wrapper).
 * @param url - The API endpoint to post to.
 * @param data - The data to send in the request body.
 * @returns A promise resolving to the axios response.
 */
export async function postInternalWithRedirect<T = any>(
  url: string,
  data?: any
): Promise<AxiosResponse<T>> {
  const response = await Axios.post<T & { redirected_to?: string }>(
    url,
    data,
    parseAuthenticationHeader()
  )

  // Check for a redirection and fetch from the redirected URL if present
  if (response.data.redirected_to) {
    const redirectedResponse = await Axios.get<T>(response.data.redirected_to)

    return redirectedResponse
  }

  return response
}
